import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './App.css';

// Language Context
const LanguageContext = React.createContext();

// Project Data
const projectsData = {
  'australian-build': {
    title: { pt: 'Australian Build', en: 'Australian Build' },
    description: { pt: 'Projeto residencial contemporâneo com design moderno', en: 'Contemporary residential project with modern design' },
    images: [
      '/assets/images/Lotvision_cgi_Australian build.png',
      '/assets/images/Lotvision_cgi_Australian build close.png'
    ],
    tags: { pt: ['Renderização', 'Exterior'], en: ['Rendering', 'Exterior'] }
  },
  'northridge': {
    title: { pt: 'Northridge', en: 'Northridge' },
    description: { pt: 'Desenvolvimento residencial moderno com múltiplas unidades', en: 'Modern residential development with multiple units' },
    images: [
      '/assets/images/Lotvision_cgi_Northridge 1.png',
      '/assets/images/Lotvision_cgi_Northridge 2.png',
      '/assets/images/Lotvision_cgi_Northridge 3.png',
      '/assets/images/Lotvision_cgi_Northridge 4.png',
      '/assets/images/Lotvision_cgi_Northridge 5.png',
      '/assets/images/Lotvision_cgi_Northridge 6.png',
      '/assets/images/Lotvision_cgi_Northridge 7.png'
    ],
    tags: { pt: ['Ilustrações', 'Arquitetura'], en: ['Illustrations', 'Architecture'] }
  },
  'spain-villa': {
    title: { pt: 'Spain Villa', en: 'Spain Villa' },
    description: { pt: 'Villa de luxo mediterrânea com vista panorâmica', en: 'Mediterranean luxury villa with panoramic view' },
    images: [
      '/assets/images/Lotvision_cgi_Spain1.png',
      '/assets/images/Lotvision_cgi_Spain2.png',
      '/assets/images/Lotvision_cgi_Spain3.png',
      '/assets/images/Lotvision_cgi_Spain4.png',
      '/assets/images/Lotvision_cgi_Spain5.png'
    ],
    tags: { pt: ['Renderização', 'Luxo'], en: ['Rendering', 'Luxury'] }
  },
  'mallorca': {
    title: { pt: 'Mallorca', en: 'Mallorca' },
    description: { pt: 'Residência costeira com design contemporâneo', en: 'Coastal residence with contemporary design' },
    images: [
      '/assets/images/Lotvision_cgi_mallorca01.png',
      '/assets/images/Lotvision_cgi_mallorca02.png'
    ],
    tags: { pt: ['Exterior', 'Vista'], en: ['Exterior', 'View'] }
  },
  'vitra-centrum': {
    title: { pt: 'Vitra Centrum', en: 'Vitra Centrum' },
    description: { pt: 'Espaço comercial com design sofisticado', en: 'Commercial space with sophisticated design' },
    images: [
      '/assets/images/Lotvision_cgi_Vitra Centrum01.png',
      '/assets/images/Lotvision_cgi_Vitra Centrum02.png',
      '/assets/images/Lotvision_cgi_Vitra Centrum03.png'
    ],
    tags: { pt: ['Comercial', 'Interior'], en: ['Commercial', 'Interior'] }
  },
  'wakefield': {
    title: { pt: 'Wakefield', en: 'Wakefield' },
    description: { pt: 'Projeto residencial elegante', en: 'Elegant residential project' },
    images: [
      '/assets/images/Lotvision_cgi_Wakefield 1.png',
      '/assets/images/Lotvision_cgi_Wakefield 2.png',
      '/assets/images/Lotvision_cgi_Wakefield 3.png',
      '/assets/images/Lotvision_cgi_Wakefield 4.png'
    ],
    tags: { pt: ['Residencial', 'Moderno'], en: ['Residential', 'Modern'] }
  },
  'boemi-store': {
    title: { pt: 'Boemi Store', en: 'Boemi Store' },
    description: { pt: 'Loja conceito com design inovador', en: 'Concept store with innovative design' },
    images: [
      '/assets/images/Renan_cgi_Boemi store scene 1.jpg',
      '/assets/images/Renan_cgi_Boemi store scene 2.jpg',
      '/assets/images/Renan_cgi_Boemi store scene 3.jpg',
      '/assets/images/Renan_cgi_Boemi store scene 4.jpg',
      '/assets/images/Renan_cgi_Boemi store scene 5.jpg',
      '/assets/images/Renan_cgi_Boemi store scene 6.jpg'
    ],
    tags: { pt: ['Comercial', 'Interior'], en: ['Commercial', 'Interior'] }
  }
};

function App() {
  const [language, setLanguage] = useState('pt');

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      <Router>
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/project/:projectId" element={<ProjectPage />} />
        </Routes>
      </Router>
    </LanguageContext.Provider>
  );
}

// Main Page Component
const MainPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="App">
      <LanguageSelector />
      <Header mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <Hero />
      <About />
      <Portfolio />
      <Services />
      <Process />
      <Contact />
      <Footer />
    </div>
  );
};

// Project Detail Page
const ProjectPage = () => {
  const { projectId } = useParams();
  const { language } = React.useContext(LanguageContext);
  const navigate = useNavigate();
  const project = projectsData[projectId];
  const [selectedImage, setSelectedImage] = useState(0);

  if (!project) {
    return <div>Projeto não encontrado</div>;
  }

  return (
    <div className="project-page">
      <LanguageSelector />
      <button className="back-button" onClick={() => navigate('/')} data-testid="back-button">
        ← Voltar
      </button>
      
      <div className="project-detail-container">
        <motion.div
          className="project-header"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1>{project.title[language]}</h1>
          <p>{project.description[language]}</p>
          <div className="project-tags">
            {project.tags[language].map((tag, i) => (
              <span key={i} className="tag">{tag}</span>
            ))}
          </div>
        </motion.div>

        <div className="project-gallery">
          <motion.div
            className="main-image"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <img src={project.images[selectedImage]} alt={project.title[language]} />
          </motion.div>

          <div className="thumbnail-grid">
            {project.images.map((image, index) => (
              <motion.div
                key={index}
                className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                onClick={() => setSelectedImage(index)}
                whileHover={{ scale: 1.05 }}
                data-testid={`thumbnail-${index}`}
              >
                <img src={image} alt={`${project.title[language]} ${index + 1}`} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Language Selector
const LanguageSelector = () => {
  const { language, setLanguage } = React.useContext(LanguageContext);

  return (
    <div className="language-selector">
      <button
        className={`lang-btn ${language === 'pt' ? 'active' : ''}`}
        onClick={() => setLanguage('pt')}
        data-testid="lang-pt-btn"
      >
        PT
      </button>
      <button
        className={`lang-btn ${language === 'en' ? 'active' : ''}`}
        onClick={() => setLanguage('en')}
        data-testid="lang-en-btn"
      >
        EN
      </button>
    </div>
  );
};

// Header Component
const Header = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const { language } = React.useContext(LanguageContext);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = {
    pt: ['Sobre', 'Projetos', 'Serviços', 'Processo', 'Contato'],
    en: ['About', 'Projects', 'Services', 'Process', 'Contact']
  };

  const navLinks = ['about', 'portfolio', 'services', 'process', 'contact'];

  return (
    <header className={`header ${scrolled ? 'scrolled' : ''}`} data-testid="main-header">
      <div className="container">
        <div className="logo">
          <a href="/">
            <img src="/assets/images/logo.png" alt="Lot Vision" />
          </a>
        </div>
        <nav className="nav desktop-nav">
          {navItems[language].map((item, index) => (
            <a
              key={index}
              href={`#${navLinks[index]}`}
              className="nav-link"
              data-testid={`nav-${navLinks[index]}`}
            >
              {item}
            </a>
          ))}
        </nav>
        <div
          className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <nav className="mobile-nav">
            {navItems[language].map((item, index) => (
              <a
                key={index}
                href={`#${navLinks[index]}`}
                className="nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const Hero = () => {
  const { language } = React.useContext(LanguageContext);

  const content = {
    pt: {
      title: 'Lot Vision',
      subtitle: 'Estúdio de visualização arquitetônica especializado em transformar conceitos em imagens que inspiram',
      cta: 'Ver Projetos'
    },
    en: {
      title: 'Lot Vision',
      subtitle: 'Architectural visualization studio specialized in transforming concepts into inspiring images',
      cta: 'View Projects'
    }
  };

  return (
    <section id="home" className="hero" data-testid="hero-section">
      <motion.div
        className="hero-content"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <h1 className="hero-title" data-testid="hero-title">{content[language].title}</h1>
        <p className="hero-subtitle">{content[language].subtitle}</p>
        <a href="#portfolio" className="cta-button" data-testid="hero-cta">
          {content[language].cta}
        </a>
      </motion.div>
      <div className="scroll-indicator">
        <div className="scroll-arrow"></div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  const { language } = React.useContext(LanguageContext);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const content = {
    pt: {
      title: 'Estúdio',
      text1: 'Na Lot Vision, transformamos conceitos arquitetônicos em visualizações de tirar o fôlego. Nossa missão é capturar a essência de cada projeto e traduzir ideias em imagens que inspiram.',
      text2: 'Com uma equipe internacional e uma abordagem inovadora, exploramos referências globais para criar visualizações que vão além do comum. Nossa paixão por arquitetura nos motiva a entregar resultados com precisão técnica e apelo estético.'
    },
    en: {
      title: 'Studio',
      text1: 'At Lot Vision, we transform architectural concepts into breathtaking visualizations. Our mission is to capture the essence of each project and translate ideas into inspiring images.',
      text2: 'With an international team and an innovative approach, we explore global references to create visualizations that go beyond the ordinary. Our passion for architecture motivates us to deliver results with technical precision and aesthetic appeal.'
    }
  };

  return (
    <section id="about" className="about" data-testid="about-section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">{content[language].title}</h2>
          <div className="section-divider"></div>
        </motion.div>
        <div className="about-content">
          <motion.div
            className="about-text"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p>{content[language].text1}</p>
            <p>{content[language].text2}</p>
          </motion.div>
          <motion.div
            className="about-image"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <img src="/assets/images/Lotvision_cgi_Spain2.png" alt="Studio" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Portfolio Section with Hover Slideshow
const Portfolio = () => {
  const { language } = React.useContext(LanguageContext);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const navigate = useNavigate();

  const content = {
    pt: { title: 'Projetos' },
    en: { title: 'Projects' }
  };

  const projectsList = Object.keys(projectsData);

  return (
    <section id="portfolio" className="portfolio" data-testid="portfolio-section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">{content[language].title}</h2>
          <div className="section-divider"></div>
        </motion.div>
        <div className="portfolio-grid">
          {projectsList.map((projectId, index) => (
            <PortfolioItem
              key={projectId}
              projectId={projectId}
              project={projectsData[projectId]}
              index={index}
              inView={inView}
              navigate={navigate}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Portfolio Item with Slideshow on Hover
const PortfolioItem = ({ projectId, project, index, inView, navigate }) => {
  const { language } = React.useContext(LanguageContext);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    let interval;
    if (isHovered && project.images.length > 1) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
      }, 2000); // Mudado de 800ms para 2000ms (2 segundos)
    } else {
      setCurrentImageIndex(0);
    }
    return () => clearInterval(interval);
  }, [isHovered, project.images.length]);

  return (
    <motion.div
      className="portfolio-item"
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => navigate(`/project/${projectId}`)}
      data-testid={`portfolio-item-${index}`}
    >
      <div className="portfolio-image">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentImageIndex}
            src={project.images[currentImageIndex]}
            alt={project.title[language]}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </AnimatePresence>
        <div className="portfolio-overlay">
          <h3>{project.title[language]}</h3>
          <p className="view-project">Ver Projeto →</p>
        </div>
      </div>
    </motion.div>
  );
};

// Services Section
const Services = () => {
  const { language } = React.useContext(LanguageContext);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const content = {
    pt: {
      title: 'Serviços',
      services: [
        { title: 'Ilustrações', image: '/assets/images/Lotvision_cgi_Wakefield 2.png' },
        { title: 'Plantas Humanizadas', image: '/assets/images/Lotvision_cgi_Vitra Centrum02.png' },
        { title: 'Tour 360º', image: '/assets/images/Renan_cgi_Boemi store scene 2.jpg' },
        { title: 'Imagens de Produto', image: '/assets/images/Renan_cgi_Boemi store scene 3.jpg' },
        { title: 'Vídeos', image: '/assets/images/Lotvision_cgi_Northridge 5.png' },
        { title: 'Modelagem 3D', image: '/assets/images/Lotvision_cgi_mallorca02.png' }
      ]
    },
    en: {
      title: 'Services',
      services: [
        { title: 'Illustrations', image: '/assets/images/Lotvision_cgi_Wakefield 2.png' },
        { title: 'Humanized Floor Plans', image: '/assets/images/Lotvision_cgi_Vitra Centrum02.png' },
        { title: '360° Virtual Tour', image: '/assets/images/Renan_cgi_Boemi store scene 2.jpg' },
        { title: 'Product Images', image: '/assets/images/Renan_cgi_Boemi store scene 3.jpg' },
        { title: 'Videos', image: '/assets/images/Lotvision_cgi_Northridge 5.png' },
        { title: '3D Modeling', image: '/assets/images/Lotvision_cgi_mallorca02.png' }
      ]
    }
  };

  return (
    <section id="services" className="services" data-testid="services-section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">{content[language].title}</h2>
          <div className="section-divider"></div>
        </motion.div>
        <div className="services-grid">
          {content[language].services.map((service, index) => (
            <motion.div
              key={index}
              className="service-item"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              data-testid={`service-item-${index}`}
            >
              <img src={service.image} alt={service.title} className="service-media" />
              <h3>{service.title}</h3>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Process Section
const Process = () => {
  const { language } = React.useContext(LanguageContext);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const content = {
    pt: {
      title: 'Processo',
      steps: [
        { number: '01', title: 'Alinhamento', description: 'Entendemos suas necessidades e objetivos do projeto.' },
        { number: '02', title: 'Preparação', description: 'Organizamos arquivos, referências e iniciamos o cronograma.' },
        { number: '03', title: 'Criação', description: 'Modelamos ambientes 3D com atenção aos detalhes.' },
        { number: '04', title: 'Validação', description: 'Apresentamos prévia para coleta de feedback.' },
        { number: '05', title: 'Refinamento', description: 'Aplicamos materiais e texturas buscando realismo.' },
        { number: '06', title: 'Entrega', description: 'Renderização em alta resolução e entrega final.' }
      ]
    },
    en: {
      title: 'Process',
      steps: [
        { number: '01', title: 'Alignment', description: 'We understand your needs and project objectives.' },
        { number: '02', title: 'Preparation', description: 'We organize files, references and start the timeline.' },
        { number: '03', title: 'Creation', description: 'We model 3D environments with attention to detail.' },
        { number: '04', title: 'Validation', description: 'We present preview for feedback collection.' },
        { number: '05', title: 'Refinement', description: 'We apply materials and textures seeking realism.' },
        { number: '06', title: 'Delivery', description: 'High-resolution rendering and final delivery.' }
      ]
    }
  };

  return (
    <section id="process" className="process" data-testid="process-section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">{content[language].title}</h2>
          <div className="section-divider"></div>
        </motion.div>
        <div className="process-grid">
          {content[language].steps.map((step, index) => (
            <motion.div
              key={index}
              className="process-step"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              data-testid={`process-step-${index}`}
            >
              <div className="step-number">{step.number}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const { language } = React.useContext(LanguageContext);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formStatus, setFormStatus] = useState('');

  const content = {
    pt: {
      title: 'Contato',
      subtitle: 'Vamos conversar sobre seu próximo projeto',
      namePlaceholder: 'Nome',
      emailPlaceholder: 'E-mail',
      phonePlaceholder: 'Telefone',
      messagePlaceholder: 'Mensagem',
      submitButton: 'Enviar',
      successMessage: 'Mensagem enviada com sucesso',
      errorMessage: 'Erro ao enviar mensagem'
    },
    en: {
      title: 'Contact',
      subtitle: 'Let\'s talk about your next project',
      namePlaceholder: 'Name',
      emailPlaceholder: 'E-mail',
      phonePlaceholder: 'Phone',
      messagePlaceholder: 'Message',
      submitButton: 'Send',
      successMessage: 'Message sent successfully',
      errorMessage: 'Error sending message'
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      const response = await fetch('https://formspree.io/f/mpwrddav', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        setFormStatus('success');
        e.target.reset();
        setTimeout(() => setFormStatus(''), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="contact" data-testid="contact-section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">{content[language].title}</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">{content[language].subtitle}</p>
        </motion.div>
        <div className="contact-content">
          <motion.div
            className="contact-form"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="form-group">
                <input type="text" name="name" required data-testid="contact-name" />
                <label>{content[language].namePlaceholder}</label>
              </div>
              <div className="form-group">
                <input type="email" name="email" required data-testid="contact-email" />
                <label>{content[language].emailPlaceholder}</label>
              </div>
              <div className="form-group">
                <input type="tel" name="phone" required data-testid="contact-phone" />
                <label>{content[language].phonePlaceholder}</label>
              </div>
              <div className="form-group">
                <textarea name="message" required data-testid="contact-message"></textarea>
                <label>{content[language].messagePlaceholder}</label>
              </div>
              <button type="submit" className="submit-btn" data-testid="contact-submit">
                {content[language].submitButton}
              </button>
              {formStatus && (
                <div className={`form-message ${formStatus}`}>
                  {formStatus === 'success' ? content[language].successMessage : content[language].errorMessage}
                </div>
              )}
            </form>
          </motion.div>
          <motion.div
            className="contact-info"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="contact-item">
              <div className="contact-icon">
                <img src="/assets/icons/email.svg" alt="Email" style={{ width: '24px', height: '24px' }} />
              </div>
              <div className="contact-details">
                <h4>E-mail</h4>
                <p>lotvisionstudio@gmail.com</p>
              </div>
            </div>
            <a
              href="https://wa.me/5589994060505?text=Olá%2C%20gostaria%20de%20iniciar%20um%20projeto"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-item contact-link"
            >
              <div className="contact-icon">
                <img src="/assets/icons/whatsapp.svg" alt="WhatsApp" style={{ width: '24px', height: '24px' }} />
              </div>
              <div className="contact-details">
                <h4>WhatsApp</h4>
                <p>(89) 99406-0505</p>
              </div>
            </a>
            <a
              href="https://www.instagram.com/lotvision"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-item contact-link"
            >
              <div className="contact-icon">
                <img src="/assets/icons/instagram.svg" alt="Instagram" style={{ width: '24px', height: '24px' }} />
              </div>
              <div className="contact-details">
                <h4>Instagram</h4>
                <p>@lotvision</p>
              </div>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const { language } = React.useContext(LanguageContext);

  const content = {
    pt: { tagline: 'Estúdio de visualização arquitetônica', rights: 'Todos os direitos reservados' },
    en: { tagline: 'Architectural visualization studio', rights: 'All rights reserved' }
  };

  return (
    <footer className="footer" data-testid="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-logo">
            <img src="/assets/images/logo.png" alt="Lot Vision" />
          </div>
          <div className="footer-info">
            <p>{content[language].tagline}</p>
          </div>
          <div className="footer-social">
            <a href="https://www.instagram.com/lotvision" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <img src="/assets/icons/instagram.svg" alt="Instagram" style={{ width: '20px', height: '20px' }} />
            </a>
            <a href="https://wa.me/5589994060505" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
              <img src="/assets/icons/whatsapp.svg" alt="WhatsApp" style={{ width: '20px', height: '20px' }} />
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2024 Lot Vision. {content[language].rights}</p>
        </div>
      </div>
    </footer>
  );
};

export default App;
